import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Heart, ImageOff, Plus, Star } from 'lucide-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { cn } from '@tsgecart/ui';
import { formatPaise } from '../../../../lib/format.js';
import { useWishlist, useWishlistMutations } from '../../wishlist/hooks/use-wishlist.js';
import { useCartMutations } from '../../cart/hooks/use-cart.js';
import type { ProductListItem } from '../types/catalog.js';

export interface ProductCardProps {
  product: ProductListItem;
  className?: string;
  /**
   * Optional single-variant id for a quick "add to cart" affordance.
   * `ProductListItem` carries no variant data, so this only appears when
   * the caller (a page that already resolved the product's variants,
   * e.g. via a richer feed) explicitly supplies one. Products with zero
   * or multiple variants simply don't render the quick-add button --
   * the card still links through to the product detail page, which is
   * the source of truth for variant selection.
   */
  singleVariantId?: string;
}

/**
 * The single reusable product card for every listing surface (shop,
 * category, search, related products). `ProductListItem` carries
 * `imageUrl` (resolved server-side from the product's primary gallery
 * image, see `resolvePrimaryImageUrl` in the worker's
 * product-repository.ts), so the real image renders when present, falling
 * back to the branded placeholder on a null/broken URL rather than ever
 * showing a broken `<img>`. It also carries `mrpFrom`/`mrpTo` (see
 * `resolveProductMrpRange`) and `inStock` (see `resolveHasAvailableStock`)
 * -- both real, resolved fields, never fabricated -- which drive the
 * discount badge (only rendered when `mrpTo` genuinely exceeds `priceTo`)
 * and the out-of-stock overlay below.
 */
export function ProductCard({ product, className, singleVariantId }: ProductCardProps) {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const { data: wishlist } = useWishlist();
  const { add: addWishlist, remove: removeWishlist } = useWishlistMutations();
  const { add: addToCart } = useCartMutations();
  const [justAdded, setJustAdded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  const wishlistItem = wishlist?.find((item) => item.productId === product.id);
  const isWishlisted = Boolean(wishlistItem);
  const wishlistPending = addWishlist.isPending || removeWishlist.isPending;

  function handleWishlistToggle(event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (!isSignedIn) {
      navigate(ROUTE_NAMES.signIn);
      return;
    }
    if (wishlistItem) {
      removeWishlist.mutate(wishlistItem.id);
    } else {
      addWishlist.mutate(product.id);
    }
  }

  function handleQuickAdd(event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (!singleVariantId) return;
    if (!isSignedIn) {
      navigate(ROUTE_NAMES.signIn);
      return;
    }
    addToCart.mutate(
      { variantId: singleVariantId, quantity: 1 },
      {
        onSuccess: () => {
          setJustAdded(true);
          setTimeout(() => setJustAdded(false), 1500);
        },
      },
    );
  }

  const priceLabel = formatPriceLabel(product);
  const discountPercent = computeDiscountPercent(product);

  return (
    <Link
      to={`${ROUTE_NAMES.product}/${product.slug}`}
      className={cn(
        'focus-visible:ring-brand group relative flex flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white p-3 transition-all duration-200 hover:-translate-y-1 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 sm:p-4',
        className,
      )}
    >
      <div className="ring-1 ring-inset ring-black/[0.03] relative aspect-square overflow-hidden rounded-2xl bg-gray-50">
        {product.imageUrl && !imageFailed ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            loading="lazy"
            onError={() => setImageFailed(true)}
            className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
          />
        ) : (
          <ProductImagePlaceholder />
        )}

        <div className="absolute left-2 top-2 flex flex-col items-start gap-1.5">
          {discountPercent !== null && (
            <span className="inline-flex items-center rounded-full bg-rose-600 px-2 py-0.5 text-[11px] font-bold text-white shadow-sm">
              {`${discountPercent}% OFF`}
            </span>
          )}
          {product.isFeatured && (
            <span className="bg-brand text-brand-foreground inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold shadow-sm">
              <Star className="h-3 w-3 fill-current" aria-hidden="true" />
              Featured
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={handleWishlistToggle}
          disabled={wishlistPending}
          aria-pressed={isWishlisted}
          aria-label={isWishlisted ? `Remove ${product.name} from wishlist` : `Add ${product.name} to wishlist`}
          className="focus-visible:ring-brand absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-gray-500 shadow-sm transition hover:bg-white disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2"
        >
          <Heart className={cn('h-4 w-4', isWishlisted && 'fill-red-500 text-red-500')} aria-hidden="true" />
        </button>

        {singleVariantId && product.inStock && (
          <button
            type="button"
            onClick={handleQuickAdd}
            disabled={addToCart.isPending}
            aria-label={`Add ${product.name} to cart`}
            className="bg-brand text-brand-foreground focus-visible:ring-brand absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full shadow-sm transition hover:opacity-90 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
          </button>
        )}

        {justAdded && (
          <span className="absolute inset-x-0 bottom-0 bg-black/70 py-1 text-center text-[11px] font-medium text-white">
            Added to cart
          </span>
        )}

        {!product.inStock && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60">
            <span className="rounded-full bg-gray-900/80 px-3 py-1 text-xs font-semibold text-white">
              Out of stock
            </span>
          </div>
        )}
      </div>

      <p className="mt-3 line-clamp-2 text-sm font-semibold text-gray-900">{product.name}</p>
      <div className="mt-1.5 flex items-baseline gap-2">
        <p className="text-base font-bold text-gray-900">{priceLabel}</p>
        {discountPercent !== null && product.mrpTo && (
          <p className="text-xs font-medium text-gray-400 line-through">
            {formatPaise(product.mrpTo.amountPaise)}
          </p>
        )}
      </div>
    </Link>
  );
}

/** `null` unless `mrpTo` genuinely exceeds `priceTo` -- never an invented percentage. */
function computeDiscountPercent(product: ProductListItem): number | null {
  if (!product.mrpTo || !product.priceTo) return null;
  const mrp = product.mrpTo.amountPaise;
  const selling = product.priceTo.amountPaise;
  if (mrp <= selling) return null;
  return Math.round(((mrp - selling) / mrp) * 100);
}

function ProductImagePlaceholder() {
  return (
    <div
      className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100"
      aria-hidden="true"
    >
      <ImageOff className="h-8 w-8 text-gray-300" />
    </div>
  );
}

function formatPriceLabel(product: ProductListItem): string {
  if (!product.priceFrom || !product.priceTo) return 'Price unavailable';
  if (product.priceFrom.amountPaise === product.priceTo.amountPaise) {
    return formatPaise(product.priceFrom.amountPaise);
  }
  return `${formatPaise(product.priceFrom.amountPaise)} – ${formatPaise(product.priceTo.amountPaise)}`;
}

/** Skeleton placeholder matching `ProductCard`'s footprint for listing grids. */
export function ProductCardSkeleton() {
  return (
    <div
      className="flex flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white p-3 sm:p-4"
      aria-hidden="true"
    >
      <div className="aspect-square animate-pulse rounded-2xl bg-gray-100" />
      <div className="mt-3 h-4 w-3/4 animate-pulse rounded bg-gray-100" />
      <div className="mt-2 h-4 w-1/3 animate-pulse rounded bg-gray-100" />
    </div>
  );
}
